/**
 * <ForgotPasswordPage> — « Mot de passe oublié ? » (page).
 *
 * Un champ : l'adresse à laquelle part le lien de réinitialisation. Sous l'envoi, la sortie que
 * l'app préfère : la connexion par code, qui se passe de mot de passe.
 *
 * Contrôlée : la valeur du champ, son message de validation, le refus du serveur et l'envoi en
 * cours arrivent en props. Ce qui suit un envoi réussi n'est pas de cette page.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput, textInputAutofill } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { AuthHeading } from "../../molecules/AuthHeading"
import { AuthTemplate } from "../../templates/AuthTemplate"

const TITLE = "Mot de passe oublié ?"
const INTRO =
    "Entrez l'adresse email avec laquelle vous vous êtes inscrit. Nous allons vous envoyer un email avec un lien " +
    "pour réinitialiser votre mot de passe."
const EMAIL_PLACEHOLDER = "Adresse email"
const SUBMIT_LABEL = "Envoyer"
const CODE_HINT = "Pas besoin de mot de passe : recevez un code par email ou par SMS."
const CODE_LABEL = "Recevoir un code"

export type ForgotPasswordPageProps = {
    email: string
    onChangeEmail: (email: string) => void
    /** Le message de validation du champ (« Email invalide »), posé par le câblage à l'envoi. */
    emailError?: string
    /** Le refus du serveur, dans ses mots. */
    serverError?: string
    /** Le lien est en cours d'envoi : le bouton tourne et ne répond plus. */
    submitting?: boolean
    onSubmit: () => void
    /** « Recevoir un code » : la connexion par code. */
    onRequestCode: () => void
    onBack?: () => void
    testID?: string
}

export function ForgotPasswordPage(props: ForgotPasswordPageProps) {
    const { email, onChangeEmail, emailError, serverError, submitting = false, onSubmit, onRequestCode, onBack } = props

    // La touche « Envoyer » du clavier ne passe pas par le bouton, qui seul se bloque pendant l'envoi.
    const submit = () => {
        if (!submitting) onSubmit()
    }

    return (
        <AuthTemplate onBack={onBack} testID={props.testID}>
            <Stack gap="8">
                <Stack gap="4">
                    <AuthHeading title={TITLE}>{INTRO}</AuthHeading>

                    <TextInput
                        {...textInputAutofill.email}
                        size="lg"
                        value={email}
                        onChangeText={onChangeEmail}
                        placeholder={EMAIL_PLACEHOLDER}
                        accessibilityLabel={EMAIL_PLACEHOLDER}
                        // Le refus du serveur porte sur cette adresse : il se lit sous elle, comme la validation.
                        error={emailError ?? serverError}
                        returnKeyType="send"
                        onSubmitEditing={submit}
                    />

                    <Button label={SUBMIT_LABEL} fullWidth loading={submitting} onPress={submit} />
                </Stack>

                <Stack gap="3">
                    <Text center color="text-muted">
                        {CODE_HINT}
                    </Text>
                    <Button
                        variant="outline"
                        iconName="envelope"
                        label={CODE_LABEL}
                        fullWidth
                        onPress={onRequestCode}
                    />
                </Stack>
            </Stack>
        </AuthTemplate>
    )
}
